import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth-context";
import { RequireAuth, RedirectIfAuthed, PendingGate } from "@/routes/guards";
import { isAppHost } from "@/config";
import { HomeScreen } from "@/features/home/HomeScreen";
import { SignInScreen } from "@/features/auth/SignInScreen";
import { PendingScreen } from "@/features/auth/PendingScreen";
import { OperationsScreen } from "@/features/ops/OperationsScreen";
import { AccountScreen } from "@/features/account/AccountScreen";
import { DeliveryScreen } from "@/features/account/DeliveryScreen";
import { SuggestionsScreen } from "@/features/suggestions/SuggestionsScreen";
import { SignalDetailRoute } from "@/features/signal/SignalDetailRoute";
import { NotFoundScreen } from "@/features/error/ErrorScreens";
import { TermsScreen } from "@/features/legal/TermsScreen";
import { PrivacyScreen } from "@/features/legal/PrivacyScreen";
import { AdminScreen } from "@/features/admin/AdminScreen";
import { ProofScreen } from "@/features/legal/ProofScreen";

/**
 * Same SPA, two domains:
 *   · earlywire.io      (apex)        → marketing HOME page
 *   · app.earlywire.io  (subdomain)   → the DASHBOARD (sign-in → ops)
 * The host decides what "/" renders — both are served by this one Netlify site.
 */
function RootRoute() {
  return isAppHost() ? <Navigate to="/ops" replace /> : <HomeScreen />;
}

/** Router-agnostic app body: the client wraps it in BrowserRouter (App below), the build-time
 *  prerender wraps it in StaticRouter (entry-server.tsx) — same components either way. */
export function AppRoutes() {
  return (
    <AuthProvider>
      <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route
            path="/signin"
            element={
              <RedirectIfAuthed>
                <SignInScreen />
              </RedirectIfAuthed>
            }
          />
          {/* /pending is reachable while authenticated-but-not-approved */}
          <Route path="/pending" element={<PendingGate><PendingScreen /></PendingGate>} />
          <Route
            path="/ops"
            element={
              <RequireAuth>
                <OperationsScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/delivery"
            element={
              <RequireAuth>
                <DeliveryScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/suggestions"
            element={
              <RequireAuth>
                <SuggestionsScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminScreen />
              </RequireAuth>
            }
          />
          <Route path="/terms" element={<TermsScreen />} />
          <Route path="/privacy" element={<PrivacyScreen />} />
          <Route path="/proof" element={<ProofScreen />} />
          <Route path="/signal/:id" element={<SignalDetailRoute />} />
          <Route path="/event/:id" element={<SignalDetailRoute />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
    </AuthProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
